import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import { RouterProvider } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import { GoogleOAuthProvider } from "@react-oauth/google"
import * as Sentry from "@sentry/react"
import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { PersistGate } from "redux-persist/integration/react"

import { TooltipProvider } from "@/components/ds/Tooltip"
import { persistor, store } from "@/store/store.ts"

import ChatwootWidget from "./components/ChatwootWidget.tsx"
import CustomErrorBoundary from "./components/CustomErrorBoundary.tsx"
import router from "./routes/router.tsx"

import "@/i18n/config.ts"

import "react-toastify/dist/ReactToastify.css"
import "./index.css"

if (!import.meta.env.DEV) {
  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY)
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: "2025-05-24",
    })
  Sentry.init({
    dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  })
}

// Content Studio mock mode (MPE-1445): `vite` dev with VITE_CONTENT_STUDIO_MOCK=1 only; never in a build.
if (import.meta.env.DEV && import.meta.env.VITE_CONTENT_STUDIO_MOCK === "1") {
  void import("@/store/creatives/mock/devConsole").then(({ registerContentStudioMock }) =>
    registerContentStudioMock(store),
  )
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
const appRoutes = (
  <>
    <RouterProvider router={router} />
    <ChatwootWidget />
    <ToastContainer limit={1} />
  </>
)

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <CustomErrorBoundary>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <TooltipProvider>
              {googleClientId ? (
                <GoogleOAuthProvider clientId={googleClientId}>{appRoutes}</GoogleOAuthProvider>
              ) : (
                appRoutes
              )}
            </TooltipProvider>
          </PersistGate>
        </Provider>
      </CustomErrorBoundary>
    </PostHogProvider>
  </StrictMode>,
)
